import React, { useState, useEffect } from 'react';
import './CourseFormModal.css';

const FALLBACK_IMG =
  'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=60';

const DEFAULT_FORM = {
  title: '',
  category: 'web development',
  duration: '3 Months',
  level: 'Beginner',
  projects: 2,
  internship: 'Guaranteed',
  price: 4999,
  order: 1,
  imageUrl: '',
  isActive: true,
  description: '',
  detailedDescription: '',
  features: [''],
  syllabus: [{ module: '', content: '' }],
};

export default function CourseFormModal({
  isOpen,
  onClose,
  onSubmit,
  editingCourse,
  getImageUrl,
  submitting,
}) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [activeSection, setActiveSection] = useState('basic');

  /* Hydrate on open */
  useEffect(() => {
    if (!isOpen) return;
    if (editingCourse) {
      setForm({
        title: editingCourse.title || '',
        category: editingCourse.category || '',
        duration: editingCourse.duration || '',
        level: editingCourse.level || 'Beginner',
        projects: editingCourse.projects || 0,
        internship: editingCourse.internship || 'N/A',
        price: editingCourse.price || 0,
        order: editingCourse.order || 0,
        imageUrl: editingCourse.imageUrl || '',
        isActive:
          editingCourse.isActive !== undefined ? editingCourse.isActive : true,
        description: editingCourse.description || '',
        detailedDescription: editingCourse.detailedDescription || '',
        features:
          editingCourse.features?.length > 0 ? editingCourse.features : [''],
        syllabus:
          editingCourse.syllabus?.length > 0
            ? editingCourse.syllabus.map((s) => ({
                module: s.module,
                content: s.content,
              }))
            : [{ module: '', content: '' }],
      });
    } else {
      setForm(DEFAULT_FORM);
    }
    setActiveSection('basic');
  }, [isOpen, editingCourse]);

  /* Lock body scroll when open */
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  /* Close on ESC */
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  /* ---------- Dynamic array handlers ---------- */
  const handleFeatureChange = (i, v) => {
    const features = [...form.features];
    features[i] = v;
    update({ features });
  };
  const addFeature = () => update({ features: [...form.features, ''] });
  const removeFeature = (i) =>
    update({ features: form.features.filter((_, idx) => idx !== i) });

  const handleSyllabusChange = (i, field, v) => {
    const syllabus = [...form.syllabus];
    syllabus[i] = { ...syllabus[i], [field]: v };
    update({ syllabus });
  };
  const addSyllabus = () =>
    update({ syllabus: [...form.syllabus, { module: '', content: '' }] });
  const removeSyllabus = (i) =>
    update({ syllabus: form.syllabus.filter((_, idx) => idx !== i) });

  /* ---------- Submit ---------- */
  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      category: form.category.toLowerCase(),
      projects: Number(form.projects),
      price: Number(form.price),
      order: Number(form.order),
      features: form.features.filter((f) => f.trim() !== ''),
      syllabus: form.syllabus.filter(
        (s) => s.module.trim() !== '' && s.content.trim() !== ''
      ),
    };
    onSubmit(payload);
  };

  const SECTION_TABS = [
    { id: 'basic', label: '📋 Basic' },
    { id: 'details', label: '⚙️ Details' },
    { id: 'descriptions', label: '📝 Descriptions' },
    { id: 'features', label: `✨ Features (${form.features.filter(Boolean).length})` },
    { id: 'syllabus', label: `📖 Syllabus (${form.syllabus.filter((s) => s.module).length})` },
  ];

  return (
    <div
      className="CourseFormModal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className="CourseFormModal-panel">
        {/* Header */}
        <div className="CourseFormModal-header">
          <div>
            <h3>
              {editingCourse
                ? '✏️ Edit Course Blueprint'
                : '🚀 Create New Course'}
            </h3>
            <span className="CourseFormModal-subtitle">
              {editingCourse
                ? `Editing: ${editingCourse.title}`
                : 'Fill in the details below to publish a new course'}
            </span>
          </div>
          <button
            type="button"
            className="CourseFormModal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Section Tabs */}
        <div className="CourseFormModal-tabs">
          {SECTION_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`CourseFormModal-tab ${
                activeSection === t.id ? 'active' : ''
              }`}
              onClick={() => setActiveSection(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Form Body */}
        <form
          id="course-form"
          onSubmit={handleSubmit}
          className="CourseFormModal-body"
        >
          {/* ---------- BASIC ---------- */}
          {activeSection === 'basic' && (
            <div className="CourseFormModal-section">
              <div className="CourseFormModal-grid-2">
                <div className="CourseFormModal-group">
                  <label className="CourseFormModal-label">
                    Course Title *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Master MERN Stack Web Development"
                    value={form.title}
                    onChange={(e) => update({ title: e.target.value })}
                    className="CourseFormModal-input"
                  />
                </div>
                <div className="CourseFormModal-group">
                  <label className="CourseFormModal-label">Category *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. web development"
                    value={form.category}
                    onChange={(e) => update({ category: e.target.value })}
                    className="CourseFormModal-input"
                  />
                </div>
                <div className="CourseFormModal-group">
                  <label className="CourseFormModal-label">
                    Price (INR ₹) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={form.price}
                    onChange={(e) => update({ price: e.target.value })}
                    className="CourseFormModal-input"
                  />
                </div>
                <div className="CourseFormModal-group">
                  <label className="CourseFormModal-label">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => update({ order: e.target.value })}
                    className="CourseFormModal-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ---------- DETAILS ---------- */}
          {activeSection === 'details' && (
            <div className="CourseFormModal-section">
              <div className="CourseFormModal-grid-2">
                <div className="CourseFormModal-group">
                  <label className="CourseFormModal-label">Duration *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 6 Months"
                    value={form.duration}
                    onChange={(e) => update({ duration: e.target.value })}
                    className="CourseFormModal-input"
                  />
                </div>
                <div className="CourseFormModal-group">
                  <label className="CourseFormModal-label">Level *</label>
                  <select
                    value={form.level}
                    onChange={(e) => update({ level: e.target.value })}
                    className="CourseFormModal-input"
                  >
                    <option>Beginner</option>
                    <option>Intermediate</option>
                    <option>Advanced</option>
                    <option>All Levels</option>
                  </select>
                </div>
                <div className="CourseFormModal-group">
                  <label className="CourseFormModal-label">
                    Projects Count
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.projects}
                    onChange={(e) => update({ projects: e.target.value })}
                    className="CourseFormModal-input"
                  />
                </div>
                <div className="CourseFormModal-group">
                  <label className="CourseFormModal-label">Internship</label>
                  <input
                    type="text"
                    placeholder="e.g. Guaranteed 3 Months"
                    value={form.internship}
                    onChange={(e) => update({ internship: e.target.value })}
                    className="CourseFormModal-input"
                  />
                </div>
              </div>

              <div className="CourseFormModal-group CourseFormModal-mt">
                <label className="CourseFormModal-label">
                  Thumbnail Image URL
                </label>
                <input
                  type="text"
                  placeholder="uploads/courses/image.jpg or https://..."
                  value={form.imageUrl}
                  onChange={(e) => update({ imageUrl: e.target.value })}
                  className="CourseFormModal-input"
                />
                {form.imageUrl && (
                  <div className="CourseFormModal-preview">
                    <img
                      src={getImageUrl(form.imageUrl)}
                      alt="Preview"
                      onError={(e) => {
                        e.target.src = FALLBACK_IMG;
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="CourseFormModal-group CourseFormModal-mt">
                <label className="CourseFormModal-label">Visibility</label>
                <label className="CourseFormModal-toggle">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => update({ isActive: e.target.checked })}
                  />
                  <span>
                    {form.isActive ? '🟢 Active' : '🔴 Inactive'}
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* ---------- DESCRIPTIONS ---------- */}
          {activeSection === 'descriptions' && (
            <div className="CourseFormModal-section">
              <div className="CourseFormModal-group">
                <label className="CourseFormModal-label">
                  Short Tagline *
                </label>
                <input
                  type="text"
                  required
                  placeholder="A brief summary for cards..."
                  value={form.description}
                  onChange={(e) => update({ description: e.target.value })}
                  className="CourseFormModal-input"
                />
              </div>
              <div className="CourseFormModal-group CourseFormModal-mt">
                <label className="CourseFormModal-label">
                  Detailed Description *
                </label>
                <textarea
                  rows="6"
                  required
                  placeholder="Comprehensive details..."
                  value={form.detailedDescription}
                  onChange={(e) =>
                    update({ detailedDescription: e.target.value })
                  }
                  className="CourseFormModal-input CourseFormModal-textarea"
                />
              </div>
            </div>
          )}

          {/* ---------- FEATURES ---------- */}
          {activeSection === 'features' && (
            <div className="CourseFormModal-section">
              <div className="CourseFormModal-row-header">
                <h4>Course Key Highlights</h4>
                <button
                  type="button"
                  className="CourseFormModal-add-btn"
                  onClick={addFeature}
                >
                  ＋ Add Highlight
                </button>
              </div>
              {form.features.map((feat, idx) => (
                <div key={idx} className="CourseFormModal-row">
                  <input
                    type="text"
                    placeholder={`Feature #${idx + 1}`}
                    value={feat}
                    onChange={(e) =>
                      handleFeatureChange(idx, e.target.value)
                    }
                    className="CourseFormModal-input"
                  />
                  {form.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(idx)}
                      className="CourseFormModal-remove-btn"
                      aria-label="Remove feature"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ---------- SYLLABUS ---------- */}
          {activeSection === 'syllabus' && (
            <div className="CourseFormModal-section">
              <div className="CourseFormModal-row-header">
                <h4>Syllabus Modules</h4>
                <button
                  type="button"
                  className="CourseFormModal-add-btn"
                  onClick={addSyllabus}
                >
                  ＋ Add Module
                </button>
              </div>
              {form.syllabus.map((item, idx) => (
                <div key={idx} className="CourseFormModal-module-card">
                  <div className="CourseFormModal-row">
                    <input
                      type="text"
                      placeholder={`Module ${idx + 1} Name`}
                      value={item.module}
                      onChange={(e) =>
                        handleSyllabusChange(idx, 'module', e.target.value)
                      }
                      className="CourseFormModal-input CourseFormModal-bold"
                    />
                    {form.syllabus.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSyllabus(idx)}
                        className="CourseFormModal-remove-btn"
                        aria-label="Remove module"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                  <textarea
                    rows="2"
                    placeholder="Module content details..."
                    value={item.content}
                    onChange={(e) =>
                      handleSyllabusChange(idx, 'content', e.target.value)
                    }
                    className="CourseFormModal-input CourseFormModal-mt-sm"
                  />
                </div>
              ))}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="CourseFormModal-footer">
          <button
            type="button"
            className="CourseFormModal-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="course-form"
            className="CourseFormModal-btn-primary"
            disabled={submitting}
          >
            {submitting
              ? '⏳ Saving...'
              : editingCourse
              ? '💾 Save Changes'
              : '🚀 Publish Course'}
          </button>
        </div>
      </div>
    </div>
  );
}